// src/pages/Clienti.jsx - Fixed with direct Supabase calls
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from '@/lib/supabase';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnimatePresence, motion } from "framer-motion";
import { Plus, Search } from "lucide-react";
import { useState } from "react";

import ClienteForm from "../components/clienti/ClienteForm";
import ClienteTable from "../components/clienti/ClienteTable";

export default function Clienti() {
  const [showForm, setShowForm] = useState(false);
  const [editingCliente, setEditingCliente] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const queryClient = useQueryClient();

  // Load clienti data
  const { data: clienti = [], isLoading } = useQuery({
    queryKey: ['clienti'],
    queryFn: async () => {
      console.log('Loading clienti...');
      const { data, error } = await supabase
        .from('clienti')
        .select('*')
        .order('nome');
      if (error) {
        console.error('Clienti loading error:', error);
        throw error;
      }
      console.log('Clienti loaded:', data.length);
      return data || [];
    }
  });

  // Create mutation
  const createClienteMutation = useMutation({
    mutationFn: async (clienteData) => {
      console.log('Creating cliente:', clienteData);
      const { data, error } = await supabase
        .from('clienti')
        .insert(clienteData)
        .select()
        .single();
      if (error) {
        console.error('Create cliente error:', error);
        throw error;
      }
      console.log('Cliente created:', data);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clienti'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    }
  });

  // Update mutation
  const updateClienteMutation = useMutation({
    mutationFn: async ({ id, data }) => {
      console.log('Updating cliente:', { id, data });
      const { data: result, error } = await supabase
        .from('clienti')
        .update(data)
        .eq('id', id)
        .select()
        .single();
      if (error) {
        console.error('Update cliente error:', error);
        throw error;
      }
      console.log('Cliente updated:', result);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clienti'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    }
  });

  // Delete mutation
  const deleteClienteMutation = useMutation({
    mutationFn: async (id) => {
      console.log('Deleting cliente:', id);
      const { error } = await supabase
        .from('clienti')
        .delete()
        .eq('id', id);
      if (error) {
        console.error('Delete cliente error:', error);
        throw error;
      }
      console.log('Cliente deleted');
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clienti'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard-data'] });
    }
  });

  // Filter clienti based on search term
  const filteredClienti = clienti.filter(cliente => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      cliente.nome?.toLowerCase().includes(searchLower) ||
      cliente.cognome?.toLowerCase().includes(searchLower) ||
      cliente.email?.toLowerCase().includes(searchLower) ||
      cliente.telefono?.includes(searchTerm)
    );
  });

  const handleSubmit = async (clienteData) => {
    try {
      if (editingCliente) {
        await updateClienteMutation.mutateAsync({ id: editingCliente.id, data: clienteData });
      } else {
        await createClienteMutation.mutateAsync(clienteData);
      }
      setShowForm(false);
      setEditingCliente(null);
    } catch (error) {
      console.error("Errore nel salvataggio:", error);
      alert(`Errore nel salvataggio: ${error.message}`);
    }
  };

  const handleEdit = (cliente) => {
    setEditingCliente(cliente);
    setShowForm(true);
  };

  const handleDelete = async (cliente) => {
    if (window.confirm("Sei sicuro di voler eliminare questo cliente?")) {
      try {
        await deleteClienteMutation.mutateAsync(cliente.id);
      } catch (error) {
        console.error("Errore nell'eliminazione:", error);
        alert(`Errore nell'eliminazione: ${error.message}`);
      }
    }
  };

  const isAnyMutationPending = 
    createClienteMutation.isPending || 
    updateClienteMutation.isPending || 
    deleteClienteMutation.isPending;

  return (
    <div className="p-4 md:p-8 min-h-screen">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 mb-2">Gestione Clienti</h1>
            <p className="text-slate-600">Gestisci i clienti del parcheggio</p>
            {/* Debug info */}
            {/* 
            <div className="text-xs text-gray-500 mt-2">
              Debug: Clienti={clienti.length}, Loading={isLoading.toString()}
            </div>
            */}
          </div>
          <Button
            onClick={() => setShowForm(!showForm)}
            className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg"
            disabled={isAnyMutationPending}
          >
            <Plus className="w-5 h-5 mr-2" />
            {isAnyMutationPending ? 'Salvando...' : 'Nuovo Cliente'}
          </Button>
        </div>

        <AnimatePresence>
          {showForm && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-8"
            >
              <ClienteForm
                cliente={editingCliente}
                onSubmit={handleSubmit}
                onCancel={() => {
                  setShowForm(false);
                  setEditingCliente(null);
                }}
                isLoading={isAnyMutationPending}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Cerca clienti..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 glass-effect border-0 shadow-lg"
            />
          </div>
        </div>

        <ClienteTable
          clienti={filteredClienti}
          onEdit={handleEdit}
          onDelete={handleDelete}
          isLoading={isLoading}
          deleteLoading={deleteClienteMutation.isPending}
        />
      </div>
    </div>
  );
}